import { mutateAsync, requestAsync } from 'redux-query';
import { updateSession } from 'modules/session';
import {
  ActionType,
  RQLoginResponse,
  ResponseStatus,
  PatientLoginInfo,
  LoginSuccess,
  Logout,
  LogoutSuccess,
  Dispatch,
} from 'types';
import { loginQuery, logoutQuery, getCurrentPatient } from './patientQueries';

const loginSuccess = ({ chartId, userId }: { chartId: number; userId: number }): LoginSuccess => ({
  payload: { chartId, userId },
  type: ActionType.LoginSuccess,
});

/**
 * @param options.explicitChartSelection - if true, an active chart will only be
 * set if the passport account only has one chart associated with it. If the account
 * has multiple charts, we'll need to ask the user in a subsequent action to make
 * an explicit selection for which chart they want to proceed as.
 */
export const login = (
  loginInfo: PatientLoginInfo,
  practiceId: number,
  options: { explicitChartSelection?: boolean } = {},
) => (dispatch: Dispatch): Promise<RQLoginResponse> => {
  return dispatch(mutateAsync(loginQuery(loginInfo, practiceId))).then(
    (response: RQLoginResponse): Promise<RQLoginResponse> => {
      if (response.body.status === ResponseStatus.success) {
        const { explicitChartSelection = false } = options;
        let chartId = null;
        if (!explicitChartSelection || response.body.data.charts.length === 1) {
          chartId = response.body.data.charts[0].id;
        }
        dispatch(
          loginSuccess({
            chartId,
            userId: response.body.data.id,
          }),
        );
        return Promise.resolve(response);
      }
      return Promise.reject(response);
    },
  );
};

export const fetchCurrentPatient = (practiceId: string | number) => (
  dispatch: Dispatch,
): Promise<RQLoginResponse> => {
  return dispatch(requestAsync(getCurrentPatient(practiceId))).then(
    (response: RQLoginResponse): Promise<RQLoginResponse> => {
      if (response.body.status === ResponseStatus.success) {
        dispatch(
          updateSession({
            activeChartId: response.body.data.charts[0].id,
            activeUserId: response.body.data.id,
          }),
        );
        return Promise.resolve(response);
      }
      return Promise.reject(response);
    },
  );
};

export const logoutSuccess = (): LogoutSuccess => ({
  type: ActionType.Logout,
});

export const logout: Logout = () => (dispatch: Dispatch): Promise<void> => {
  return dispatch(mutateAsync(logoutQuery())).then(
    (): Promise<void> => {
      dispatch(logoutSuccess());

      return Promise.resolve();
    },
  );
};
